import { useAppSelector } from "features/store";
import { usePermissions } from "hooks/usePermissions";
import { useViewAs } from "hooks/useViewAs";
import { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import type { SurfaceKey } from "types/interfaces/access";
import { openAction } from "utils/access";

type RequireAccessProps = {
  children: ReactNode;
  /**
   * Decide by the logged-in user rather than by the page's viewed user: entering
   * Client View is the viewer's own action, the viewed client never holds it.
   */
  bySignedInUser?: boolean;
} & (
  | { surface: SurfaceKey; action?: never }
  | { action: string; surface?: never }
);

/**
 * One route guard for every page: a surface (its `.open` action) or an explicit
 * action. A refused direct link lands on Home, or on the Client View home.
 */
const RequireAccess = ({
  children,
  surface,
  action,
  bySignedInUser = false,
}: RequireAccessProps): JSX.Element => {
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);
  const { isClientView, basePath } = useViewAs();
  const { sees, holds } = usePermissions();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (user && !user.is_pass_reset) {
    return <Navigate to="/reset-password" replace />;
  }
  const key = action ?? openAction(surface as SurfaceKey);
  const allowed = bySignedInUser ? holds(key) : sees(key);
  if (!user || !allowed) {
    const stayInShell = !bySignedInUser && isClientView && basePath;
    return <Navigate to={stayInShell ? basePath : "/"} replace />;
  }
  return <>{children}</>;
};

export default RequireAccess;
